package songwritersnotebook.app.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.VolumeOff
import androidx.compose.material.icons.automirrored.filled.VolumeUp
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.painterResource
import songwritersnotebookapp.composeapp.generated.resources.Res
import songwritersnotebookapp.composeapp.generated.resources.ic_guitar
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import songwritersnotebook.app.viewmodels.EditorViewModel
import songwritersnotebook.app.viewmodels.RecordingTarget
import songwritersnotebook.app.utils.AudioHandler
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class, androidx.compose.foundation.layout.ExperimentalLayoutApi::class)
@Composable
fun RecordingSheet(
    target: RecordingTarget,
    lineText: String?,
    audioHandler: AudioHandler,
    outputFilePath: String,
    initialAudioUri: String?,
    onDismiss: () -> Unit,
    onSave: (String, String) -> Unit,
    onDelete: () -> Unit,
    editorViewModel: EditorViewModel
) {
    val uiState by editorViewModel.uiState.collectAsStateWithLifecycle()
    var isRecording by remember { mutableStateOf(false) }
    var hasRecording by remember { mutableStateOf(initialAudioUri != null) }
    var isPlaying by remember { mutableStateOf(false) }
    var recordingName by remember { mutableStateOf(target.name) }
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }

    val startRecordingAction = {
        hasRecording = false
        audioHandler.startRecording(outputFilePath)
        isRecording = true
    }

    val micPermissionLauncher = songwritersnotebook.app.utils.rememberMicPermissionLauncher(
        onGranted = startRecordingAction,
        onDenied = {
            scope.launch {
                snackbarHostState.showSnackbar("Microphone permission is required to record audio.")
            }
        }
    )

    val startRecording = {
        micPermissionLauncher()
    }

    val stopRecording = {
        scope.launch {
            audioHandler.stopRecording()
            isRecording = false
            hasRecording = true
        }
        Unit
    }

    val togglePlayback = {
        if (isPlaying) {
            audioHandler.stopPlayback()
            isPlaying = false
        } else {
            isPlaying = true
            audioHandler.playAudio(outputFilePath) {
                isPlaying = false
            }
        }
    }

    DisposableEffect(Unit) {
        onDispose {
            audioHandler.stopPlayback()
            audioHandler.stopRecordingNonBlocking()
        }
    }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
        dragHandle = { BottomSheetDefaults.DragHandle() },
        modifier = Modifier.fillMaxHeight(0.95f)
    ) {
        val scrollState = rememberScrollState()
        Box(modifier = Modifier.fillMaxSize().navigationBarsPadding()) {
            Column(
                modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // --- 1. FIXED HEADER ---
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center,
                    modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)
                ) {
                    Text("Record Audio", style = MaterialTheme.typography.titleLarge)
                    
                    val recStatus = uiState.uploadStatus[target.id]
                    val recordingSaveStatus = when (recStatus) {
                        songwritersnotebook.app.database.UploadStatus.UPLOADING, songwritersnotebook.app.database.UploadStatus.PENDING -> songwritersnotebook.app.viewmodels.SaveStatus.SAVING
                        songwritersnotebook.app.database.UploadStatus.SUCCESS -> songwritersnotebook.app.viewmodels.SaveStatus.SAVED
                        songwritersnotebook.app.database.UploadStatus.ERROR -> songwritersnotebook.app.viewmodels.SaveStatus.ERROR
                        else -> songwritersnotebook.app.viewmodels.SaveStatus.IDLE
                    }

                    SaveStatusIndicator(
                        saveStatus = recordingSaveStatus,
                        isCloudSyncEnabled = uiState.isShared || (uiState.song?.collaborators?.isNotEmpty() == true) || (uiState.song?.viewers?.isNotEmpty() == true) || (uiState.song?.collaboratorEmails?.isNotEmpty() == true),
                        onClick = { 
                            if (recStatus == songwritersnotebook.app.database.UploadStatus.ERROR) {
                                uiState.song?.recordings?.values?.flatten()?.find { it.id == target.id }?.let {
                                    editorViewModel.retryUpload(it)
                                }
                            }
                        }
                    )
                }
                
                OutlinedTextField(
                    value = recordingName,
                    onValueChange = { recordingName = it },
                    label = { Text("Recording Name") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)
                )

                // --- Instrument Toggle ---
                Row(
                    modifier = Modifier
                        .padding(bottom = 16.dp)
                        .height(32.dp)
                        .border(
                            width = 1.dp,
                            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f),
                            shape = RoundedCornerShape(50)
                        ),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    val pianoColors = if (uiState.selectedInstrument == "piano") ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)
                    val guitarColors = if (uiState.selectedInstrument == "guitar") ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)

                    Button(
                        onClick = { editorViewModel.setInstrument("piano") },
                        shape = RoundedCornerShape(topStartPercent = 50, bottomStartPercent = 50),
                        modifier = Modifier.fillMaxHeight().width(56.dp),
                        contentPadding = PaddingValues(0.dp),
                        colors = pianoColors
                    ) {
                        Icon(Icons.Filled.Piano, contentDescription = "Piano", modifier = Modifier.size(18.dp))
                    }

                    Box(modifier = Modifier.fillMaxHeight().width(1.dp).background(MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)))

                    Button(
                        onClick = { editorViewModel.setInstrument("guitar") },
                        shape = RoundedCornerShape(topEndPercent = 50, bottomEndPercent = 50),
                        modifier = Modifier.fillMaxHeight().width(56.dp),
                        contentPadding = PaddingValues(0.dp),
                        colors = guitarColors
                    ) {
                        Icon(painter = painterResource(Res.drawable.ic_guitar), contentDescription = "Guitar", modifier = Modifier.size(18.dp))
                    }
                }
                
                // --- 2. SCROLLABLE CONTENT (Lyrics & Chords) ---
                Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                    if (lineText != null) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f), MaterialTheme.shapes.medium)
                                .border(1.dp, MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f), MaterialTheme.shapes.medium)
                                .verticalScroll(scrollState)
                                .padding(16.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            // Clean Lyrics
                            val cleanText = lineText.replace(Regex("\\[.*?\\]"), "")
                            Text(
                                text = "“$cleanText”",
                                style = MaterialTheme.typography.titleMedium,
                                color = MaterialTheme.colorScheme.onSurface,
                                textAlign = androidx.compose.ui.text.style.TextAlign.Center
                            )

                            // Chord Buttons
                            val chordRegex = Regex("\\[(.*?)\\]")
                            val chords = chordRegex.findAll(lineText).map { it.groupValues[1] }.toList()

                            if (chords.isNotEmpty()) {
                                Spacer(Modifier.height(16.dp))
                                androidx.compose.foundation.layout.FlowRow(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
                                    verticalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    chords.forEach { chordName ->
                                        val isChordInLibrary = uiState.song?.chordDefinitions?.containsKey(chordName) == true
                                        
                                        val containerColor = if (isChordInLibrary) songwritersnotebook.app.ui.theme.GoldAccent else MaterialTheme.colorScheme.surfaceVariant
                                        val contentColor = if (isChordInLibrary) androidx.compose.ui.graphics.Color.Black else MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f)
                                        
                                        OutlinedButton(
                                            onClick = { if (isChordInLibrary) editorViewModel.playChordByName(chordName) },
                                            modifier = Modifier.padding(horizontal = 4.dp),
                                            shape = RoundedCornerShape(8.dp),
                                            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                                            border = BorderStroke(width = 2.dp, color = androidx.compose.ui.graphics.Color.Transparent),
                                            colors = ButtonDefaults.buttonColors(
                                                containerColor = containerColor,
                                                contentColor = contentColor,
                                                disabledContainerColor = MaterialTheme.colorScheme.surfaceVariant,
                                                disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f)
                                            ),
                                            enabled = isChordInLibrary
                                        ) {
                                            Text(chordName, fontFamily = androidx.compose.ui.text.font.FontFamily.Monospace)
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        // Fallback if no lyrics (e.g. general recording)
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            Icon(Icons.Default.Mic, null, modifier = Modifier.size(64.dp), tint = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                        }
                    }
                }

                // --- 3. FIXED CONTROLS (Metronome & Buttons) ---
                Column(
                    modifier = Modifier.fillMaxWidth().padding(top = 16.dp, bottom = 8.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    MetronomeControls(editorViewModel)

                    Box(contentAlignment = Alignment.Center, modifier = Modifier.height(100.dp)) {
                        if (isRecording) {
                            FilledIconButton(onClick = stopRecording, modifier = Modifier.size(80.dp)) {
                                Icon(Icons.Default.Stop, "Stop Recording", modifier = Modifier.size(40.dp))
                            }
                        } else {
                            FilledIconButton(onClick = if (hasRecording) togglePlayback else startRecording, modifier = Modifier.size(80.dp)) {
                                val icon = if (hasRecording) if (isPlaying) Icons.Default.Stop else Icons.Default.PlayArrow else Icons.Default.Mic
                                Icon(icon, "Interaction Button", modifier = Modifier.size(40.dp))
                            }
                        }
                    }

                    if (hasRecording && !isRecording) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceEvenly
                        ) {
                            TextButton(onClick = startRecording) {
                                Icon(Icons.Default.Refresh, null, modifier = Modifier.padding(end = 4.dp))
                                Text("Re-record")
                            }
                            TextButton(onClick = onDelete, colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error)) {
                                Icon(Icons.Default.Delete, null, modifier = Modifier.padding(end = 4.dp))
                                Text("Delete")
                            }
                        }
                    }

                    Button(
                        onClick = { onSave(outputFilePath, recordingName) },
                        enabled = hasRecording && !isRecording && recordingName.isNotBlank(),
                        modifier = Modifier.fillMaxWidth().height(56.dp),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text("Save Recording", fontWeight = FontWeight.Bold)
                    }
                }
            }

            SnackbarHost(
                hostState = snackbarHostState,
                modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 16.dp)
            )
        }
    }
}

@Composable
private fun MetronomeControls(
    editorViewModel: EditorViewModel
) {
    val uiState by editorViewModel.uiState.collectAsStateWithLifecycle()
    val isEnabled = uiState.isMetronomeEnabled
    val isMuted = uiState.isMetronomeMuted

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(IntrinsicSize.Min)
            .border(
                1.dp,
                MaterialTheme.colorScheme.outline.copy(alpha = 0.5f),
                MaterialTheme.shapes.medium
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TextButton(
            onClick = { editorViewModel.toggleMetronomeEnabled() },
            modifier = Modifier.weight(0.7f),
            colors = if (isEnabled) {
                ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.primary)
            } else {
                ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        ) {
            Icon(Icons.Default.Timer, contentDescription = "Toggle Metronome")
            Spacer(Modifier.width(8.dp))
            Text("Metronome")
        }

        VerticalDivider(
            modifier = Modifier
                .fillMaxHeight()
                .width(1.dp),
            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
        )

        IconButton(
            onClick = { editorViewModel.toggleMetronomeMuted() },
            enabled = isEnabled
        ) {
            Icon(
                if (isMuted) Icons.AutoMirrored.Filled.VolumeOff else Icons.AutoMirrored.Filled.VolumeUp,
                contentDescription = "Mute Metronome"
            )
        }

        Spacer(modifier = Modifier.weight(0.3f))

        MetronomeVisualizer(
            beat = uiState.currentMetronomeBeat,
            modifier = Modifier.padding(end = 16.dp)
        )
    }
}
